import React, { useEffect, useState } from 'react'

import { Button } from 'components/ui/button'
import { IconWarning } from 'components/ui/icons'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'
import { IssueItem } from './issueItem'

import { connect } from 'containers/store'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore, GettyAppState } from 'domains/org/getty/store'
import { IssueMesages } from 'domains/org/getty/utils/validation'

import { Page } from 'generated/mos/pagination'
import { StopPointOfInterest, Stop, Tour, TourCategory, Mood } from 'generated/mos/org/getty/audio'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { pick } from 'helpers/core'
import { statusSelector } from 'helpers/status'
import { space } from 'helpers/style'

import styled from 'styled'

type ActionProps = Pick<typeof actionCreators, 'gettyListAllValidationFailuresRequest'>

type ConnectedProps = Pick<GettyAppState, 'allValidationFailuresList'>

type DirectProps = {
  projectId: string;
}

type Props = ConnectedProps & DirectProps & ActionProps

const Issues = styled.div`
  margin-bottom:  ${space(4)};
  & > * {
    margin-top: ${space(4)};
  }
`

const Icon = styled(IconWarning)`
  color: ${({ theme }) => theme.color.warning};
  margin-right: ${space(2)};
`

const ValidationFailuresListView = (props: Props) => {
  const {
    gettyListAllValidationFailuresRequest,
    allValidationFailuresList,
    projectId,
  } = props

  const [ show, setShow ] = useState<boolean>(false)

  const data = statusSelector.data(allValidationFailuresList)
  const publicBundle = data && data.summaries.find(({ bundleType }) => bundleType === 'BUNDLE_TYPE_PUBLIC')
  const issueCount = publicBundle && publicBundle.issueCount

  useEffect(() => {
    gettyListAllValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 10000,
      },
      filter: {
        ...ListValidationFailuresRequest.ByBundleType.defaults,
        bundleTypes: ['BUNDLE_TYPE_PUBLIC']
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const makeWarning = (failure: ValidationFailure.Entity) => {
    const issues = failure && failure.issues
    const title = failure && failure.entityName
    const failureRef = failure.ref
    const failureType = failureRef && failureRef.typename
    if(issues && failureType && failureRef) {
      if(failureType === StopPointOfInterest.refName) {
        return <IssueItem title={title} text={`The following fields are required to publish this point of interest: ${issues.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.`} />
      }
      if(failureType === Stop.refName) {
        return <IssueItem title={title} text={`The following fields are required to publish this stop: ${issues.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.`} link={`/project/${projectId}/stop/${failureRef.id}` } />
      }
      if(failureType === Mood.refName) {
        const failureFields = issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE')
        const invalidColors = issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'INVALID_COLOR')
        return (
          <>
            {failureFields && <IssueItem title={title} text={`The following fields are required to publish this mood: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.`} link={`/project/${projectId}/mood/${failureRef.id}` } />}
            {invalidColors && <IssueItem title={title} text={`The following fields have invalid colors for this mood: ${invalidColors.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.`} link={`/project/${projectId}/mood/${failureRef.id}` } />}
          </>
        )
      }
      if(failureType === Tour.refName) {
        const failureFields = issues.filter(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'MISSING_REQUIRED_ATTRIBUTE' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')
        const failureTourStops = issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'TOUR_CONTAINS_STOPS_FROM_MULTIPLE_SITES' || issueType === 'TOUR_CONTAINS_NO_STOPS' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')
        const failureTourStopsMessage = failureTourStops && IssueMesages[failureTourStops.issueType]
        return (
          <>
            {failureFields && <IssueItem title={title} text={`The following fields are required to publish this tour: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.`} link={`/project/${projectId}/tour/${failureRef.id}/tour-details` } />}
            {failureTourStopsMessage && <IssueItem title={title} text={failureTourStopsMessage} link={`/project/${projectId}/tour/${failureRef.id}` } />}
          </>
        )
      }
      if(failureType === TourCategory.refName) {
        const failureFields = issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'TOUR_CATEGORY_CONTAINS_TOURS_FROM_MULTIPLE_SITES')
        const failureMessage = failureFields && IssueMesages[failureFields.issueType]
        return failureMessage && <IssueItem title={title} text={failureMessage} link={`/project/${projectId}/categories`} />
      }
    }
    return null
  }

  return (
    <>
      {!!issueCount &&
        <Button appearance="secondary" onClick={() => setShow(true)}>
          <Icon />
          {issueCount} issues
        </Button>
      }
      {show &&
        <Modal onClose={() => setShow(false)} header={<h3>{issueCount} issues</h3>}>
          <StatusGuard status={allValidationFailuresList}>
            <StatusSummary warning={[{ text: 'These issues must be fixed before publishing' }]} />
            <Issues>
              {data && data.failures.map(issue => makeWarning(issue))}
            </Issues>
          </StatusGuard>
        </Modal>
      }
    </>
  )
}

export const ValidationFailuresList = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  ({ getty }) => pick(getty, 'allValidationFailuresList'),
  pick(actionCreators, 'gettyListAllValidationFailuresRequest')
)(ValidationFailuresListView)
